import type { ReactNode, Ref } from 'react';
import type { ProspectBookQuery } from '@signalvote/shared';
import { Link } from '@tanstack/react-router';
import { useFundraisingTabCounts } from '@/hooks/queries';
import { cn } from '@/lib/utils';

/** Which Fundraising tab a page is. */
export type FundraisingTab = 'prospects' | 'call-books' | 'match-review' | 'commitments' | 'follow-ups';

const count = new Intl.NumberFormat('en-US');

const TABS: { tab: FundraisingTab; label: string; to: string }[] = [
  { tab: 'prospects', label: 'Prospects', to: '/admin/fundraising' },
  { tab: 'call-books', label: 'Call books', to: '/admin/fundraising/call-books' },
  { tab: 'match-review', label: 'Match review', to: '/admin/fundraising/match-review' },
  { tab: 'commitments', label: 'Open commitments', to: '/admin/fundraising/commitments' },
  { tab: 'follow-ups', label: 'Follow-ups', to: '/admin/fundraising/follow-ups' },
];

/**
 * Every Fundraising page: the title, the five tabs with their counts, and the page's primary action on
 * the right. The counts are the whole campaign's — everyone, the call books, the possible matches and the
 * open commitments — so they're the same whichever tab is showing: the unfiltered book's summary, reused
 * between tabs, or on the Prospects page (which passes its `book`) that book's own counts while nothing
 * filters it (`useFundraisingTabCounts`).
 *
 * `actionRef` is where a page that keeps its action's state elsewhere (the Prospects page's Import
 * people, which lives with the upload overlay) portals it in.
 */
export function FundraisingPage({
  tab,
  action,
  actionRef,
  book,
  children,
}: {
  tab: FundraisingTab;
  /** The Prospects book the page shows, whose own counts are the tab counts when nothing filters it. */
  book?: ProspectBookQuery;
  action?: ReactNode;
  actionRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  const { data: tabCounts } = useFundraisingTabCounts(book);
  const counts: Record<FundraisingTab, number | null> = {
    prospects: tabCounts?.counts.everyone ?? null,
    'call-books': tabCounts?.callBooks ?? null,
    'match-review': tabCounts?.counts.possibleMatch ?? null,
    commitments: tabCounts?.counts.openCommitment ?? null,
    'follow-ups': null,
  };

  return (
    <div className="min-h-full">
      <header className="border-b bg-card px-4 pt-6 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-[22px] font-bold leading-7 tracking-tight text-foreground">Fundraising</h1>
          <div ref={actionRef} className="flex flex-wrap items-center gap-2">
            {action}
          </div>
        </div>
        <nav aria-label="Fundraising" className="mt-3 flex gap-7 overflow-x-auto">
          {TABS.map(({ tab: key, label, to }) => {
            const current = key === tab;
            const n = counts[key];
            return (
              <Link
                key={key}
                to={to}
                aria-current={current ? 'page' : undefined}
                className={cn(
                  'flex h-11 shrink-0 items-center gap-1.5 border-b-2 text-[13px] font-semibold transition-colors',
                  current ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
                {n !== null && <span className="text-xs font-medium tabular-nums text-muted-foreground">{count.format(n)}</span>}
              </Link>
            );
          })}
        </nav>
      </header>
      <div className="px-4 py-5 sm:px-8">{children}</div>
    </div>
  );
}
